import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { formatFileSize, openFileExternally } from '@/constants/utils/files';
import { videoMediaOrigin } from '@/constants/utils/video_project';
import { coworkerApi } from '@/lib/api/coworker';
import useArtifactEditConversation from '@/hooks/useArtifactEditConversation';
import VideoPreviewPlayer from './VideoPreviewPlayer';

const clock = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

function Action({ label, icon, onPress, disabled, colors, children }) {
  return <Pressable accessibilityRole="button" accessibilityLabel={label} disabled={disabled}
    style={({ hovered }) => [styles.action, { borderColor: colors.border_color_secondary }, hovered && { backgroundColor: colors.solid_color_tertiary }, disabled && { opacity: 0.4 }]}
    onPress={onPress}>
    <Icon name={icon} size={14} color={colors.text_color_primary} />
    {children ? <Text size="xs" weight="medium">{children}</Text> : null}
  </Pressable>;
}

// Identity keys remount playback and pending work when another file is selected.
export default function VideoPreviewPanel({ file, onClose }) {
  const identity = [file?.id, file?.taskId, file?.path, file?.cdnPath, file?.url].join(':');
  return <VideoPreviewContent key={identity} file={file} onClose={onClose} />;
}

function VideoPreviewContent({ file, onClose }) {
  const { colors } = useTheme();
  const editConversation = useArtifactEditConversation();
  const title = file?.name || file?.originalName || 'Video';
  const directUrl = file?.cdnPath || file?.url || file?.uri || '';
  const taskId = file?.taskId || file?.parameters?.workTaskId;
  const path = file?.path || file?.parameters?.workArtifactPath;
  const [url, setUrl] = useState(directUrl);
  const [metadata, setMetadata] = useState(file?.metadata?.video || {});
  const [loading, setLoading] = useState(!directUrl);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const recovering = useRef(false);
  const blobUrl = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; if (blobUrl.current) URL.revokeObjectURL(blobUrl.current); };
  }, []);
  useEffect(() => {
    if (directUrl) return;
    let cancelled = false;
    (async () => {
      try {
        if (!taskId || !path) throw new Error('This video is not available yet.');
        const resolved = await coworkerApi.getArtifactDownloadUrl(taskId, path);
        if (!resolved) throw new Error('This video is not available yet.');
        if (!cancelled) setUrl(resolved);
      } catch (err) { if (!cancelled) setError(err.message || 'Could not load this video.'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [directUrl, taskId, path]);
  useEffect(() => {
    if (Platform.OS !== 'web' || !onClose) return;
    const close = event => {
      if (event.key === 'Escape' && !document.fullscreenElement && !['INPUT', 'TEXTAREA'].includes(event.target?.tagName)) onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose]);

  const playbackError = async () => {
    if (recovering.current) return;
    if (taskId && path && !blobUrl.current && Platform.OS === 'web') {
      recovering.current = true;
      setLoading(true);
      try {
        const response = await coworkerApi.fetchArtifactBinary(taskId, path, { asProxy: true });
        if (!response.ok) throw new Error('Video unavailable');
        const blob = await response.blob();
        if (!alive.current) return;
        blobUrl.current = URL.createObjectURL(blob);
        setUrl(blobUrl.current);
        return;
      } catch { /* Show the external playback fallback below. */ }
      finally { recovering.current = false; if (alive.current) setLoading(false); }
    }
    if (alive.current) setError('This video could not play here. Download it or open the original to watch it in another player.');
  };
  const details = useMemo(() => [
    videoMediaOrigin(file) === 'downloaded' ? 'Downloaded video' : 'Video',
    clock(metadata.duration),
    metadata.width && metadata.height ? `${metadata.width} × ${metadata.height}` : null,
    (file?.sizeInBytes || file?.size) ? formatFileSize(file.sizeInBytes || file.size) : null,
  ].filter(Boolean).join(' · '), [file, metadata]);

  const prepareEdit = () => {
    const request = instruction.trim();
    if (!request || busy) return;
    setBusy(true);
    try {
      const id = file.id || file._id;
      if (!id && !(taskId && path && file.conversationId)) throw new Error('This video is still being saved. Please try again shortly.');
      const source = id ? '' : ` (task artifact: ${path})`;
      const prompt = `Edit the video "${title}"${source}. ${request}\nKeep the original video and deliver the edited version as a separate file.`;
      const target = editConversation.captureTarget();
      if (!id && String(target.contextId) !== String(file.conversationId)) throw new Error('Open the original conversation to edit this video while it is being saved.');
      editConversation.addRequest(target, { file: id ? { ...file, id } : null, prompt, mode: 'coworker' });
      setNotice('Added to chat. Review the request, then press Send.');
      setInstruction('');
    } catch (err) { setNotice(err.message || 'Could not prepare your edit.'); }
    finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const result = await downloadMedia(url, title);
      if (alive.current && result?.success === false) setNotice('Could not download the video. Try opening the original.');
    } catch { if (alive.current) setNotice('Could not download the video. Try opening the original.'); }
  };

  return <View style={[styles.container, { backgroundColor: colors.solid_color_primary }]}>
    <View style={[styles.header, { borderColor: colors.border_color_secondary }]}>
      <View style={[styles.identity, { backgroundColor: colors.solid_color_tertiary }]}><Icon name="file-video" size={17} color={colors.text_color_secondary} /></View>
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}><Text numberOfLines={1} weight="semibold">{title}</Text><Text size="xxs" color="secondary">Video preview</Text></View>
      <Action label="Download video" icon="arrow-down-to-line" colors={colors} disabled={!url} onPress={download} />
      <Action label="Open original video" icon="arrow-up-right-from-square" colors={colors} disabled={!(directUrl || url) || (directUrl || url).startsWith('blob:')} onPress={() => openFileExternally({ ...file, cdnPath: directUrl || url })} />
      {onClose && <Action label="Close video preview" icon="xmark" colors={colors} onPress={onClose} />}
    </View>
    <View style={styles.stage}>
      {url && !error && <VideoPreviewPlayer key={url} url={url} poster={file?.thumbnailPath} title={title} onMetadata={setMetadata} onError={playbackError} />}
      {loading && <View style={styles.overlay}><ActivityIndicator color="#fff" /><Text size="s" style={{ color: '#ddd' }}>Loading video…</Text></View>}
      {!!error && <View style={styles.error}><Icon name="file-video" size={30} color="#aaa" /><Text size="s" style={{ color: '#ddd', textAlign: 'center', maxWidth: 360 }}>{error}</Text></View>}
    </View>
    <View style={[styles.footer, { borderColor: colors.border_color_secondary }]}>
      <Text size="xs" color="secondary" style={{ flex: 1 }}>{details}</Text>
      <Action label={editing ? 'Hide video editing options' : 'Edit this video'} icon={editing ? 'chevron-down' : 'wand-magic-sparkles'} colors={colors} onPress={() => setEditing(!editing)}>{editing ? 'Hide editing' : 'Edit this video'}</Action>
    </View>
    {editing && <ScrollView style={{ flexGrow: 0, maxHeight: 280 }} contentContainerStyle={styles.edit} keyboardShouldPersistTaps="handled">
      <Text weight="semibold">What would you like to change?</Text>
      <View style={styles.chips}>{[['Trim', 'Trim the video to '], ['Add captions', 'Add readable captions matching the spoken audio.'], ['Resize', 'Reframe this video to a vertical 9:16 format.']].map(([label, prompt]) => <Action key={label} label={label} icon={label === 'Trim' ? 'scissors' : label === 'Resize' ? 'expand' : 'closed-captioning'} colors={colors} onPress={() => setInstruction(prompt)}>{label}</Action>)}</View>
      <TextInput accessibilityLabel="Video editing instructions" multiline placeholder="Describe your edit, including any timestamps…" placeholderTextColor={colors.text_color_tertiary} value={instruction} onChangeText={setInstruction} style={[styles.input, { color: colors.text_color_primary, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary }]} />
      <View style={styles.footerRow}><Text size="xxs" color="secondary" style={{ flex: 1 }}>Prepare an edit request in chat.</Text><Action label="Add video edit to chat" icon="arrow-right" colors={colors} disabled={!instruction.trim() || busy} onPress={prepareEdit}>Add to chat</Action></View>
    </ScrollView>}
    {!!notice && <View accessibilityRole="alert" style={styles.notice}><Text size="xs" color="secondary">{notice}</Text></View>}
  </View>;
}

const styles = StyleSheet.create({
  container: { flex: 1, minWidth: 0, minHeight: 0 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 14, borderBottomWidth: 1 },
  identity: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: 10 },
  action: { minHeight: 36, minWidth: 36, borderRadius: 8, borderWidth: 1, paddingHorizontal: 10, flexDirection: 'row', gap: 7, alignItems: 'center', justifyContent: 'center' },
  stage: { flex: 1, minHeight: 180, backgroundColor: '#080a0c', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  overlay: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', gap: 12, backgroundColor: '#080a0c' },
  error: { alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 },
  footer: { padding: 14, borderTopWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  edit: { padding: 16, gap: 12 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  input: { minHeight: 72, maxHeight: 110, padding: 12, borderWidth: 1, borderRadius: 10, fontSize: 14, textAlignVertical: 'top' },
  footerRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  notice: { paddingHorizontal: 16, paddingBottom: 12 },
});
